import { useEffect, useState } from 'react';
import {
    Button,
    Container,
    Dimmer,
    Form,
    Header,
    Label,
    Loader,
    Segment,
    Table,
    Popup,
} from 'semantic-ui-react';
import { useAppSelector } from '../../actions/store';
import { useDispatch } from 'react-redux';
import { loadDistributorInfos } from '../../actions/partyActions';
import SearchableSelect from '../common/fields/SearchableSelect';
import { ExecutionTicketSet, Party, PartyProduct } from '../../types';
import PartyApi from '../../api/partyApi';
import toastr from 'toastr';
import ExecutionApi from '../../api/executionApi';
import { Link } from 'react-router-dom';
import * as uiActions from '../../actions/uiActions';
import { MODAL_COMPONENTS } from '../../constants/modalComponents';
import EntityApi from '../../api/entityApi';

const DistributorProductsPage = () => {
    const dispatch = useDispatch();
    const distributorInfos = useAppSelector(
        (state) => state.distributorInfos?.list,
    );

    const ownerPartyId = useAppSelector(
        (state) => state.constants.options.ownerPartyId,
    );

    const [isLoading, setIsLoading] = useState(true);
    const [isPartyProductsLoading, setIsPartyProductsLoading] = useState(false);

    const [selectedDistributorId, setSelectedDistributorId] =
        useState<string>();
    const [parentParties, setParentParties] = useState<Party[]>([]);
    const [selectedParentPartyId, setSelectedParentPartyId] =
        useState<string>();
    const [partyProducts, setPartyProducts] = useState<PartyProduct[]>();
    const [parentPartyProducts, setParentPartyProducts] =
        useState<ExecutionTicketSet[]>();
    const [availableProducts, setAvailableProducts] = useState<PartyProduct[]>(
        [],
    );

    const parentPartyIsOwner = ownerPartyId === selectedParentPartyId;

    const fetchDistributors = async () => {
        await dispatch(loadDistributorInfos());
        setIsLoading(false);
    };

    const fetchParentParties = async (parentPartiesIds: string[]) => {
        console.log('Fetching parent parties with IDs:', parentPartiesIds);
        setIsPartyProductsLoading(true);
        try {
            const res = await PartyApi.getParties({
                where: { id: { inq: parentPartiesIds } },
            });
            console.log('Parent parties fetched:', res);
            setIsPartyProductsLoading(false);
            setParentParties(res);
            if (res.length === 1) {
                console.log('Auto-selecting parent party:', res[0].id);
                setSelectedParentPartyId(res[0].id);
            }
        } catch (error) {
            console.error('Error fetching parent parties:', error);
            setIsPartyProductsLoading(false);
            toastr.error(error);
        }
    };

    const fetchPartyProducts = async () => {
        setIsPartyProductsLoading(true);
        try {
            const res = await EntityApi.getPartyProducts(
                selectedDistributorId,
                selectedParentPartyId,
            );
            setIsPartyProductsLoading(false);
            setPartyProducts(res);
        } catch (error) {
            setIsPartyProductsLoading(false);
            toastr.error(error);
        }
    };

    const fetchAvailableProducts = async () => {
        console.log(
            'Fetching available products for parent:',
            selectedParentPartyId,
        );
        setIsPartyProductsLoading(true);
        try {
            const res = await EntityApi.getPartyProducts(
                selectedParentPartyId,
                undefined,
                { where: { availableForSelection: true } },
            );
            console.log('Available products raw response:', res);
            setIsPartyProductsLoading(false);
            setAvailableProducts(res || []);
        } catch (error) {
            //console.error('Error fetching available products:', error);
            setIsPartyProductsLoading(false);
            toastr.error(`Failed to fetch available products: ${error}`);
        }
    };

    const fetchParentPartyProducts = async () => {
        setIsPartyProductsLoading(true);
        console.log(
            'Fetching parent party products for parent:',
            selectedParentPartyId,
        );
        try {
            const res = await ExecutionApi.getExecutionTicketSets(
                parentPartyIsOwner ? undefined : selectedParentPartyId,
                parentPartyIsOwner ? 'CD-FV-M' : 'CD-FV-D',
            );
            console.log('Parent party products fetched:', res);
            setIsPartyProductsLoading(false);
            setParentPartyProducts(res);
        } catch (error) {
            setIsPartyProductsLoading(false);
            toastr.error(error);
        }
    };

    const fetchTableData = () => {
        console.log('Fetching table data with:', {
            selectedDistributorId,
            selectedParentPartyId,
            parentPartyIsOwner,
        });
        fetchPartyProducts();
        fetchParentPartyProducts();
        fetchAvailableProducts();
    };

    useEffect(() => {
        fetchDistributors();
    }, []);

    useEffect(() => {
        if (selectedDistributorId) {
            const distributor = distributorInfos.find(
                (item) => item.id === selectedDistributorId,
            );
            console.log('Selected distributor:', distributor);
            fetchParentParties(distributor?.parentPartyIds?.ids);
        }
    }, [selectedDistributorId]);

    useEffect(() => {
        if (selectedParentPartyId) {
            console.log('Parent party ID changed to:', selectedParentPartyId);
            fetchTableData();
        }
    }, [selectedParentPartyId]);

    const availableProductsWithIsAddedStatus =
        availableProducts?.map((availableProduct) => {
            const added = !!partyProducts?.find(
                (product) =>
                    product.sourceExecutionTicketId ===
                    availableProduct.executionTicketId,
            );

            return { ...availableProduct, added };
        }) || [];

    //Call the Notification API to send a notification to the distributor
    const handleSendNotification = async (
        targetPartyId: string | undefined,
        ownerPartyId: string | undefined,
        productLabel: string,
    ) => {
        setIsLoading(true);

        console.log('targetPartyId', targetPartyId);
        console.log('ownerPartyId', ownerPartyId);
        console.log('productLabel', productLabel);

        if (!targetPartyId || !ownerPartyId || !productLabel) {
            toastr.error('An Error occurred while sending the notification');
            setIsLoading(false);
            return;
        }

        try {
            //This needs an Owner Party
            //Target Id
            //Product Label
            const result = await PartyApi.sendNotification({
                notificationType: 'INCOMPLETE_PRODUCT_SUBMISSION',
                targetPartyId,
                productLabel,
                ownerPartyId,
                middlePartyId: ownerPartyId, //Owner gets overridden so template uses this
            });

            console.log('result', result);

            toastr.success('Notification sent');
        } catch (e: any) {
            toastr.error(e);
        }
        setIsLoading(false);
    };

    const handleOpenAddProductsModal = () => {
        dispatch(
            uiActions.showModal({
                modalType: MODAL_COMPONENTS.ADD_PRODUCTS_MODAL,
                modalProps: {
                    title: parentPartyProducts
                        ? 'Add products'
                        : 'Add standalone products',
                    validProducts: availableProductsWithIsAddedStatus,
                    selectedDistributorId,
                    onSucess: () => {
                        fetchParentPartyProducts();
                        fetchPartyProducts();
                    },
                },
            }),
        );
    };

    return (
        <Container>
            <Dimmer active={isLoading} inverted>
                <Loader disabled={!isLoading} />
            </Dimmer>
            {!isLoading && (
                <>
                    <Header as="h2">Distributor Products</Header>
                    <Segment>
                        <Form.Input>
                            <SearchableSelect
                                label="Distributor"
                                value={selectedDistributorId}
                                options={distributorInfos}
                                valueField="id"
                                onChange={(_, { value }) => {
                                    setAvailableProducts(null);
                                    setParentPartyProducts(null);
                                    setSelectedParentPartyId(null);
                                    setPartyProducts(null);
                                    setSelectedParentPartyId(null);
                                    setSelectedDistributorId(value);
                                }}
                            />
                        </Form.Input>
                        <Form.Input>
                            <SearchableSelect
                                label="Product Source"
                                value={selectedParentPartyId}
                                options={parentParties}
                                valueField="id"
                                onChange={(_, { value }) => {
                                    setSelectedParentPartyId(value);
                                }}
                            />
                        </Form.Input>
                    </Segment>

                    <Loader active={isPartyProductsLoading && !partyProducts} />

                    <Header>
                        Distributed Products from{' '}
                        {
                            parentParties?.find(
                                (item) => item.id === selectedParentPartyId,
                            )?.label
                        }
                    </Header>
                    <Table celled>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>
                                    Product Name
                                </Table.HeaderCell>
                                <Table.HeaderCell collapsing>
                                    Class
                                </Table.HeaderCell>
                                <Table.HeaderCell collapsing>
                                    Status
                                </Table.HeaderCell>
                                <Table.HeaderCell collapsing>
                                    {' '}
                                </Table.HeaderCell>
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {partyProducts
                                ?.sort((a, b) =>
                                    a.executionTicketSetlabel.localeCompare(
                                        b.executionTicketSetlabel,
                                    ),
                                )
                                .map((product, index) => {
                                    return (
                                        <Table.Row key={index}>
                                            <Table.Cell>
                                                <Link
                                                    to={product.clientLinkPath}
                                                >
                                                    {
                                                        product.executionTicketSetlabel
                                                    }
                                                </Link>
                                            </Table.Cell>
                                            <Table.Cell collapsing>
                                                {product.productClass}
                                            </Table.Cell>
                                            <Table.Cell collapsing>
                                                <Label
                                                    color={
                                                        product?.clientStatusColor
                                                    }
                                                >
                                                    {product?.clientStatusLabel}
                                                </Label>
                                            </Table.Cell>
                                            <Table.Cell collapsing>
                                                {(product?.clientStatusColor ===
                                                    'orange' ||
                                                    product?.clientStatusColor ===
                                                        'grey') && (
                                                    <Popup
                                                        content="Send Reminder"
                                                        trigger={
                                                            <Button
                                                                basic
                                                                loading={
                                                                    isLoading
                                                                }
                                                                icon="mail"
                                                                onClick={() =>
                                                                    handleSendNotification(
                                                                        product?.partyId,
                                                                        product?.executionOwnerPartyId,
                                                                        product.executionTicketSetlabel,
                                                                    )
                                                                }
                                                                color="blue"
                                                                compact
                                                            />
                                                        }
                                                    />
                                                )}
                                            </Table.Cell>
                                        </Table.Row>
                                    );
                                })}
                        </Table.Body>
                    </Table>
                    {parentPartyProducts && partyProducts && (
                        <>
                            <Button
                                loading={isPartyProductsLoading}
                                onClick={handleOpenAddProductsModal}
                            >
                                {parentPartyProducts
                                    ? 'Add products'
                                    : 'Add standalone products'}
                            </Button>
                        </>
                    )}
                </>
            )}
        </Container>
    );
};

export default DistributorProductsPage;
